import { yupResolver } from "@hookform/resolvers/yup";
import { useQuery } from "@apollo/client";
import { StackScreenProps } from "@react-navigation/stack";
import {
  Badge,
  Card,
  FlatActionList,
  Form,
  Page,
  Button,
  Stack,
  Text,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { useEffect } from "react";
import { ActivityIndicator, View } from "react-native";
import { FormProvider, useForm } from "react-hook-form";
import { Colors, Spacings } from "react-native-ui-lib";
import { CustomerRootParamList } from "../../../../types";
import { CustomerDetailsForm } from "../../../point-of-sale/components/CustomerDetailsForm";
import {
  CustomerFormValues,
  customerInputSchema,
  defaultCustomerInputValues,
  useMutationUpdateCustomer,
  QUERY_CUSTOMER,
} from "../../../point-of-sale/utils/customer";
import { normalizeSelectedValues } from "../../../../utilities/transforms";
import {
  Customer,
  Sale,
} from "@towersystems/roam-common/lib/generated-types";
import { moneyToFormattedString } from "../../../../utilities/money";
import {
  bankersRound,
  formatDateToCalendar,
  fullDateTimeFormat,
} from "@towersystems/roam-common/lib/shared-utils";
import { QUERY_SALE_LIST } from "../../../sales/utils/sales/constants";
import { Pressable } from "react-native";

export type EditCustomerScreenProps = StackScreenProps<
  CustomerRootParamList,
  "EditCustomerScreen"
>;

export function EditCustomerScreen({
  navigation,
  route,
}: EditCustomerScreenProps) {
  const customerId = route.params.customerId;
  const { showMessage } = useFlashMessages();

  const { data } = useQuery<{ customer: Customer }>(QUERY_CUSTOMER, {
    variables: { id: customerId },
  });

  const { data: salesData, loading: salesLoading } = useQuery<{
    sales: { items: Sale[] };
  }>(QUERY_SALE_LIST, {
    variables: {
      options: {
        filter: { customerId: { in: [customerId] } },
        sort: { saleDate: "DESC" },
        limit: 10,
        page: 1,
      },
    },
  });

  const methods = useForm<CustomerFormValues>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: normalizeSelectedValues(defaultCustomerInputValues, {}),
    resolver: yupResolver(customerInputSchema),
  });

  const {
    formState: { isValid, isDirty },
  } = methods;

  useEffect(() => {
    if (data?.customer) {
      const c = data.customer;
      methods.reset(
        normalizeSelectedValues(defaultCustomerInputValues, {
          id: c.id,
          firstName: c.firstName,
          lastName: c.lastName,
          emailAddress: c.emailAddress,
          mobileNumber: c.mobileNumber,
          phoneNumber: c.phoneNumber,
          gender: c.gender,
        }),
      );
    }
  }, [data?.customer]);

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <Button
          label="Reset"
          isAction
          disabled={!isDirty}
          onPress={() => methods.reset()}
          size="compact"
        />
      ),
    });
  }, [isDirty]);

  const { updateCustomer } = useMutationUpdateCustomer();

  async function onValidSubmit(formValues: any) {
    try {
      await updateCustomer(formValues);
      showMessage({ message: "Customer updated", severity: "success" });
      navigation.goBack();
    } catch {
      showMessage({ message: "Failed to update customer", severity: "error" });
    }
  }

  const customer = data?.customer;

  const footerMarkup = (
    <Button
      label="Save"
      backgroundColor={Colors.action}
      color="white"
      disabled={!isDirty || !isValid}
      onPress={methods.handleSubmit(onValidSubmit, console.log)}
    />
  );

  const overviewMarkup = customer ? (
    <Card title="Overview">
      <FlatActionList
        items={[
          {
            key: "spent",
            title: "Total Spent",
            componentRight: () => (
              <Text variant="heading1">
                {moneyToFormattedString(bankersRound(customer.amountSpend ?? 0))}
              </Text>
            ),
          },
          {
            key: "orders",
            title: "Orders",
            componentRight: () => (
              <Text variant="heading1">{customer.numberOfOrders ?? 0}</Text>
            ),
          },
          {
            key: "invoiced",
            title: "Invoiced Amount",
            componentRight: () => (
              <Text variant="heading1">
                {moneyToFormattedString(bankersRound(customer.invoicedAmount ?? 0))}
              </Text>
            ),
          },
          {
            key: "loyalty",
            title: "Loyalty Points",
            componentRight: () => (
              <Text variant="heading1">{customer.loyaltyPoints ?? 0} pts</Text>
            ),
          },
        ]}
      />
      {(customer.isTradeCustomer || customer.isRetailerCusAccount) && (
        <View
          style={{
            flexDirection: "row",
            padding: Spacings.s4,
            gap: Spacings.s2,
          }}
        >
          {customer.isTradeCustomer && <Badge>Trade</Badge>}
          {customer.isRetailerCusAccount && <Badge>Retailer Account</Badge>}
        </View>
      )}
    </Card>
  ) : null;

  const addressesMarkup =
    customer?.addresses && customer.addresses.length > 0 ? (
      <Card title="Addresses">
        <FlatActionList
          items={customer.addresses.map((addr) => ({
            key: addr.id,
            title: [addr.address1, addr.address2].filter(Boolean).join(", "),
            subTitle: `${addr.city} ${addr.state} ${addr.postcode}, ${addr.country}`,
            componentRight: addr.isPrimary
              ? () => <Badge>Primary</Badge>
              : undefined,
          }))}
        />
      </Card>
    ) : null;

  const salesHistoryMarkup = (
    <Card title="Sales History">
      {salesLoading && (
        <View style={{ padding: Spacings.s4 }}>
          <ActivityIndicator />
        </View>
      )}
      {!salesLoading && salesData?.sales.items.length === 0 && (
        <View style={{ padding: Spacings.s4, gap: Spacings.s2 }}>
          <Text variant="heading3">No purchase history yet</Text>
          {customer?.createdAt ? (
            <Text color="subdued">
              Customer since{" "}
              {formatDateToCalendar(customer.createdAt, fullDateTimeFormat)}
            </Text>
          ) : null}
        </View>
      )}
      {salesData?.sales.items.map((sale: Sale, idx: number) => (
        <Pressable
          key={sale.id}
          onPress={() => navigation.navigate("SaleScreen", { saleId: sale.id })}
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            paddingHorizontal: Spacings.s4,
            paddingVertical: Spacings.s3,
            borderBottomWidth: idx < salesData.sales.items.length - 1 ? 1 : 0,
            borderBottomColor: Colors.borderColor,
          }}
        >
          <View style={{ flex: 1 }}>
            <Text variant="heading" fontWeight="medium">
              #{sale.invoiceNumber}
            </Text>
            <Text variant="heading4" color="subdued">
              {formatDateToCalendar(sale.saleDate, fullDateTimeFormat)}
              {sale.location?.name ? ` · ${sale.location.name}` : ""}
            </Text>
          </View>
          <View style={{ flexDirection: "row", alignItems: "center", gap: Spacings.s2 }}>
            <View style={{ alignItems: "flex-end" }}>
              <Text variant="heading1">
                {moneyToFormattedString(bankersRound(sale.totalPrice))}
              </Text>
              <Badge status={sale.state as any}>{sale.state}</Badge>
            </View>
            <Text fontWeight="bold" variant="heading1" color="subdued">›</Text>
          </View>
        </Pressable>
      ))}
    </Card>
  );

  return (
    <Page
      title={
        customer
          ? `${customer.firstName} ${customer.lastName}`
          : "Customer"
      }
      secondaryTitle="Customer Details"
      footer={footerMarkup}
    >
      <Stack vertical>
        {overviewMarkup}
        {addressesMarkup}
        <FormProvider {...methods}>
          <Form>
            <Card title="Customer Details">
              <CustomerDetailsForm />
            </Card>
          </Form>
        </FormProvider>
        {salesHistoryMarkup}
      </Stack>
    </Page>
  );
}
